import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IconArrowLeft,
  IconCreditCard,
  IconCheck,
  IconX,
  IconClock,
  IconInfoCircle,
  IconReceipt,
  IconCurrencyDollar,
  IconShoppingCart,
  IconUser
} from "@tabler/icons-react";
import Logo from "../../assets/images/paqtory.png";
import { useSelector, useDispatch } from "react-redux";
import { fetchPaymentDetails, clearCurrentPayment } from "../../redux/slices/paymentsSlice";
import Sidebar from "../../components/common/AdminSideBar";
import Header from "../../components/common/AdminHeader";
import AdminLayout from "../../components/common/AdminLayout";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from "../../components/common/LoadingFallback";
import ErrorFallback from "../../components/common/ErrorFallback";


const PaymentDetail = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayoutConfig } = useAdminLayout();


  // Redux state
  const { currentPayment, loading, error } = useSelector((state) => state.payments);

  // Fetch payment details on mount
  useEffect(() => {
    setLayoutConfig({
      title: "Payment Transaction Detail / View",
      showBackButton: true,
      scrollable: true,
    });
    dispatch(fetchPaymentDetails(transactionId));

    return () => {
      dispatch(clearCurrentPayment());
    };
  }, [dispatch, transactionId, setLayoutConfig]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return "bg-green-100 text-green-800";
      case 'PENDING':
        return "bg-yellow-100 text-yellow-800";
      case 'FAILED':
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'STRIPE':
        return "bg-blue-100 text-blue-800";
      case 'RAZORPAY':
        return "bg-purple-100 text-purple-800";
      case 'PAYPAL':
        return "bg-indigo-100 text-indigo-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) return <LoadingFallback/>;
  if (error) return <ErrorFallback onRetry={() => dispatch(fetchPaymentDetails(transactionId))} />;

  return (
    <div>
      <main className="py-6 px-4 flex-1 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Payment Summary */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
                <IconCreditCard size={20} />
                Payment Summary
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Transaction ID</p>
                  <p className="font-medium">{currentPayment?.transaction_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">
                    {currentPayment?.created_at}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <div className={`px-2 py-1 rounded inline-block ${getStatusColor(currentPayment?.status)}`}>
                    {currentPayment?.status}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Method</p>
                  <div className={`px-2 py-1 rounded inline-block ${getMethodColor(currentPayment?.method)}`}>
                    {currentPayment?.method}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="font-medium">${currentPayment?.amount}</p>
                </div>
              </div>
            </div>

            {/* Order Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
                <IconShoppingCart size={20} />
                Order Information
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Order ID</p>
                  <p className="font-medium">{currentPayment?.order_detail?.order_uid}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Customer Name</p>
                  <p className="font-medium">{currentPayment?.order_detail?.customer_full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Order Status</p>
                  <div className={`px-2 py-1 rounded inline-block ${getStatusColor(currentPayment?.order_detail?.payment_status)}`}>
                    {currentPayment?.order_detail?.status}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Status</p>
                  <div className={`px-2 py-1 rounded inline-block ${getStatusColor(currentPayment?.order_detail?.payment_status)}`}>
                    {currentPayment?.order_detail?.payment_status}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Timeline */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold border-b pb-2 mb-4 flex items-center gap-2">
            <IconClock size={20} />
            Payment Timeline
          </h3>

          <div className="relative pl-6 border-l-2 border-gray-200 space-y-4">
            <div className="relative">
              <div className="absolute -left-7 top-0 w-4 h-4 rounded-full bg-blue-500"></div>
              <div className="pl-4">
                <p className="font-medium">Payment Created</p>
                <p className="text-sm text-gray-500">
                  {currentPayment?.created_at}
                </p>
                <p className="text-sm">Status: {currentPayment?.status}</p>
              </div>
            </div>

            {currentPayment?.status === 'COMPLETED' && (
              <div className="relative">
                <div className="absolute -left-7 top-0 w-4 h-4 rounded-full bg-green-500"></div>
                <div className="pl-4">
                  <p className="font-medium">Payment Completed</p>
                  <p className="text-sm text-gray-500">
                    {currentPayment?.created_at} {/* Replace with actual completion time if available */}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Receipt Information */}
        {currentPayment?.status === 'COMPLETED' && (
          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <h3 className="text-lg font-semibold border-b pb-2 mb-4 flex items-center gap-2">
              <IconReceipt size={20} />
              Receipt Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Transaction ID</p>
                <p className="font-medium">{currentPayment?.transaction_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="font-medium">{currentPayment?.method}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Amount</p>
                <p className="font-medium">${currentPayment?.amount}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <div className={`px-2 py-1 rounded inline-block ${getStatusColor(currentPayment?.status)}`}>
                  {currentPayment?.status}
                </div>
              </div>
            </div>


          </div>
        )}
      </main>
    </div>
  );
};

export default PaymentDetail;